import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react'
import { mergeRefs } from '@lighthouse/tools'
import React, { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { PAGE_CONTAINER_HOST } from '../../../constants'
import { useFloatBoxContext } from '../../../contexts'
import { useResolveSizeLayout } from '../../../hooks/blockStyle/size'
import { getSizeStyle } from '../../../utils/layout'
import { useAppContainerContext } from '../../ApplicationContainer'
import { useContainerLayoutContext, useFlowLayoutContext } from '../Context'
import type { FlowLayoutNode, NodeRenderProps } from '../types'

type BoxProps = {
    disabled?: boolean
}

const StyledBox = styled.div<BoxProps>`
    display: flex;
    flex-direction: column;
    position: relative;
    flex-shrink: 0;

    &[data-highlight='true'] {
        background-color: #5551ff1f;
        outline: 2px solid var(--color-main);
        outline-offset: -1px;
    }
`

interface FlowLayoutOverlayNodeProps extends React.ComponentPropsWithoutRef<'div'> {
    data: FlowLayoutNode
    disabled?: boolean
    extra?: React.ReactNode
    onDataDrawerVisible?: (val: boolean) => void
    nodeRender?: (props: NodeRenderProps) => JSX.Element | null
}

export const OverlayNode = forwardRef<HTMLDivElement, FlowLayoutOverlayNodeProps>((props, ref) => {
    const { data, style, disabled: propDisabled, onDataDrawerVisible, nodeRender, children, extra, ...rest } = props
    const { size, overlay } = data.data || {}
    const { overlayType } = overlay ?? {}
    const { highlight, nodeRender: globalNodeRender = React.Fragment, rootElement } = useFlowLayoutContext()
    const NodeRender = nodeRender ?? globalNodeRender
    const frameID = useRef(0)

    const parentLayoutCtx = useContainerLayoutContext()
    const { scale = 1 } = useAppContainerContext()
    const resolvedSize = useResolveSizeLayout(size)

    const [parentNodeWidth, setParentNodeWidth] = useState(0)
    const [parentNodeHeight, setParentNodeHeight] = useState(0)
    const [parentEle, setParentEle] = useState<Element | null | undefined>(null)
    // const { width: windowWidth, height: windowHeight } = useWindowSize()
    // const { showMode } = useAppContainerContext()

    // 设置父容器初始宽度
    useLayoutEffect(() => {
        const parentEle = rootElement?.querySelector(`[data-node-id="${parentLayoutCtx.parentId}"]`)
        setParentEle(parentEle)
        setParentNodeWidth((parentEle?.getBoundingClientRect().width ?? 0) / scale)
        setParentNodeHeight((parentEle?.getBoundingClientRect().height ?? 0) / scale)
    }, [data.id, parentLayoutCtx.parentId, parentNodeWidth, rootElement, scale])

    const observer = useMemo(
        () =>
            typeof window === 'undefined'
                ? null
                : new ResizeObserver((entries: ResizeObserverEntry[]) => {
                      const entry = entries[0]

                      if (entry) {
                          cancelAnimationFrame(frameID.current)
                          frameID.current = requestAnimationFrame(() => {
                              if (parentEle) {
                                  setParentNodeWidth(entry.contentRect.width / scale)
                                  setParentNodeHeight(entry.contentRect.height / scale)
                              }
                          })
                      }
                  }),
        [parentEle, scale]
    )

    useEffect(() => {
        if (parentEle) {
            observer?.observe(parentEle)
        }
        return () => {
            observer?.disconnect()
            if (frameID.current) {
                cancelAnimationFrame(frameID.current)
            }
        }
    }, [observer, parentEle, parentLayoutCtx.parentId, rootElement])

    const widthStyle = useMemo(() => {
        if (resolvedSize?.width.size === 'fill') {
            return { width: parentNodeWidth }
        }

        if (resolvedSize?.width.unit === '%' && resolvedSize?.width.size !== 'auto') {
            return { width: (parentNodeWidth * Number(resolvedSize?.width.size ?? 0)) / 100 }
        }
        if (resolvedSize?.width.unit === 'vw') {
            const pageContainer = document.querySelector<HTMLDivElement>(PAGE_CONTAINER_HOST)
            if (!pageContainer) {
                return { width: `${resolvedSize?.width?.size ?? 0}${resolvedSize?.width?.unit || 'px'}` }
            }
            const currentSize = typeof resolvedSize?.width.size === 'number' ? resolvedSize?.width.size ?? 0 : 0
            const pageContainerRect = pageContainer.getBoundingClientRect()
            const width = Number(pageContainerRect.width * (currentSize / scale / 100)).toFixed(2)
            return { width: `${width}px` }
        }
    }, [parentNodeWidth, scale, resolvedSize?.width.size, resolvedSize?.width.unit])

    const heightStyle = useMemo(() => {
        if (resolvedSize?.height.size === 'fill') {
            return { height: parentNodeHeight }
        }

        if (resolvedSize?.height.unit === '%' && resolvedSize?.height.size !== 'auto') {
            return { height: (parentNodeHeight * Number(resolvedSize?.height.size ?? 0)) / 100 }
        }
        if (resolvedSize?.height.unit === 'vh') {
            const pageContainer = document.querySelector<HTMLDivElement>(PAGE_CONTAINER_HOST)
            if (!pageContainer) {
                return { height: `${resolvedSize?.height?.size ?? 0}${resolvedSize?.height?.unit || 'px'}` }
            }
            const currentSize = typeof resolvedSize?.height.size === 'number' ? resolvedSize?.height.size ?? 0 : 0
            const pageContainerRect = pageContainer.getBoundingClientRect()
            const height = Number(pageContainerRect.height * (currentSize / scale / 100)).toFixed(2)
            return { height: `${height}px` }
        }
    }, [parentNodeHeight, scale, resolvedSize?.height.size, resolvedSize?.height.unit])

    // 定位的逻辑
    const { context, interactionProps, opened, floating } = useFloatBoxContext(data.id)

    // 固定浮层打开时需要遮住应用导航栏
    useEffect(() => {
        const isFixedFloatBoxOpened = overlayType === 'fixed' && opened
        if (rootElement) {
            rootElement.style.zIndex = isFixedFloatBoxOpened ? '2' : '1'
        }
    }, [opened, overlayType, rootElement])

    const { x, y } = context ?? {}

    const floatStyle: React.CSSProperties = useMemo(() => {
        const styles = {
            fixed: {
                inset: 0,
                width: '100%',
                height: '100%'
            },
            relative: {
                top: y,
                left: x
            }
        }[overlayType ?? 'fixed']
        return {
            position: overlayType === 'relative' ? 'absolute' : 'fixed',
            opacity: opened ? 1 : 0,
            ...widthStyle,
            ...heightStyle,
            ...styles
        }
    }, [y, x, overlayType, opened, widthStyle, heightStyle])

    const zIndex = useMemo(() => {
        if (overlayType !== 'relative') {
            return 2
        }
        const parentEle = rootElement?.querySelector<HTMLDivElement>(`[data-node-id="${parentLayoutCtx.parentId}"]`)
        if (!parentEle) {
            return 1
        }
        if (!parentEle.style.position || parentEle.style.position === 'relative') {
            return 1
        }
        return 2
    }, [overlayType, parentLayoutCtx.parentId, rootElement])

    // 用一个延迟状态来控制 DOM 的实际挂载
    const [isMounted, setIsMounted] = useState(opened)

    useEffect(() => {
        if (opened) {
            setIsMounted(true)
        } else {
            // 延迟 100ms 卸载 DOM，完美吸收移动端后续派发的“幽灵鼠标事件”
            const timer = setTimeout(() => {
                setIsMounted(false)
            }, 100)
            return () => clearTimeout(timer)
        }
    }, [opened])

    if (!isMounted || !context) {
        return null
    }

    return (
        <FloatingFocusManager
            context={context}
            visuallyHiddenDismiss
            returnFocus
            initialFocus={-1}
            modal={false}
            closeOnFocusOut={false}
            order={['content']}
        >
            <FloatingPortal root={rootElement || document.body}>
                <StyledBox
                    ref={floating ? mergeRefs([node => node && floating(node, data.id), ref]) : ref}
                    data-type="overlay"
                    data-node-id={data.id}
                    data-highlight={!!highlight?.[data.id]?.self}
                    style={{
                        ...getSizeStyle(resolvedSize, parentLayoutCtx.layout?.align?.direction, false),
                        ...floatStyle,
                        ...style,
                        // 防止遮挡拖拽指线
                        zIndex
                    }}
                    {...rest}
                    {...interactionProps.getFloatingProps()}
                >
                    <NodeRender node={data} disabled={propDisabled}>
                        {children}
                        {extra}
                    </NodeRender>
                </StyledBox>
            </FloatingPortal>
        </FloatingFocusManager>
    )
})
